import React, { useState, useEffect, useRef } from 'react';
import { callApi } from '../api/api';
import { getCleanImageUrl, getPlaceholderImage } from '../utils/imageHelper';
import { decodeHtml, parsePrice, formatCompanyName, formatPackingInfo } from '../utils/textHelper';
import ProductCard from './ProductCard';
import RichMedicalData from './RichMedicalData';

const ProductDetailsView = ({ productId, addToCart, showMessage }) => {
    const [product, setProduct] = useState(null);
    const [substitutes, setSubstitutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [displayImg, setDisplayImg] = useState('');
    const [imageFailed, setImageFailed] = useState(false);
    const [aiData, setAiData] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiSubstitutes, setAiSubstitutes] = useState([]);
    const [aiSubsLoading, setAiSubsLoading] = useState(false);
    const aiEnrichFired = useRef(false);
    const aiSubsFired = useRef(false);

    useEffect(() => {
        if (!productId) return;

        const fetchProductData = async () => {
            setLoading(true);
            setError(null);
            setImageFailed(false);
            try {
                // Fetch product details
                const productData = await callApi(`/v2/products/${productId}`, 'GET');
                if (!productData) throw new Error("Product not found");
                setProduct(productData);

                // Fetch substitutes
                try {
                    const subsData = await callApi(`/v2/products/${productId}/substitutes`, 'GET');
                    setSubstitutes(subsData || []);
                } catch (subErr) {
                    console.error("Failed to fetch substitutes", subErr);
                }

            } catch (err) {
                console.error("Failed to load product details", err);
                setError(err.message || "Failed to load product details.");
            } finally {
                setLoading(false);
            }
        };

        fetchProductData();
        window.scrollTo(0, 0);
        aiEnrichFired.current = false;
        aiSubsFired.current = false;
        setAiData(null);
        setAiSubstitutes([]);
    }, [productId]);


    // Auto-enrich with AI when key fields are missing
    useEffect(() => {
        if (!product || aiEnrichFired.current) return;

        const productName = product.product_name || product.Product || product.productName || product.name;
        const hasMrp = parsePrice(product?.product_price || product?.MRP || product?.mrp || product?.price) || product?.scrapedMrp;
        const hasDesc = product?.medicine_desc || product?.description;
        const hasImg = (product?.imageUrl && product.imageUrl.startsWith('http') && !product.imageUrl.includes('placehold.co'))
            || product?.imageUrls?.length > 0;

        // Only call AI if a critical component (Description, Image, or MRP) is entirely missing from the DB
        if (!hasMrp || !hasDesc || !hasImg) {
            aiEnrichFired.current = true;
            setAiLoading(true);
            callApi('/ai/enrich-product', 'POST', {
                productId: product._id,
                productName,
                hasMrp: !!hasMrp,   // so backend won't overwrite real DB price
                company: product?.Company || product?.company || product?.product_manufactured,
                saltComposition: product?.saltComposition || product?.salt_composition,
                category: product?.category || product?.Category || product?.sub_category,
            })
            .then(resp => {
                if (resp?.success && resp?.data) {
                    setAiData(resp.data);
                    // State updates automatically trigger a re-render so we don't need to reload
                    // setTimeout(() => {
                    //    window.location.reload();
                    // }, 800);
                }
            })
            .catch(() => {})
            .finally(() => setAiLoading(false));
        }
    }, [product]);

    // Auto-fetch AI substitutes when DB has none
    useEffect(() => {
        if (!product || loading || aiSubsFired.current) return;
        if (substitutes.length > 0) return; // DB has substitutes, no need for AI

        const pName = product.product_name || product.Product || product.productName || product.name;
        const salt = product.saltComposition || product.salt_composition || aiData?.saltComposition;

        // Only fire after enrichment data is available (for salt) or if salt exists in DB
        if (!pName) return;

        aiSubsFired.current = true;
        setAiSubsLoading(true);
        callApi('/ai/get-substitutes', 'POST', {
            productName: pName,
            saltComposition: salt || '',
            company: product?.Company || product?.company || product?.product_manufactured,
        })
        .then(resp => {
            if (resp?.success) {
                // Prefer DB matches (real products), supplement with AI suggestions
                if (resp.dbMatches?.length > 0) {
                    setSubstitutes(resp.dbMatches);
                } else if (resp.aiSubstitutes?.length > 0) {
                    setAiSubstitutes(resp.aiSubstitutes);
                }
            }
        })
        .catch(() => {})
        .finally(() => setAiSubsLoading(false));
    }, [product, substitutes, loading, aiData]);

    const getProductField = (...fields) => fields.reduce((value, field) => value || (product && product[field]), undefined);
    
    const productName = decodeHtml(getProductField('product_name', 'Product', 'productName', 'name') || '');
    const mrpValue = parsePrice(product?.product_price || product?.MRP || product?.mrp || product?.price)
        || (aiData?.mrp ? parseFloat(aiData.mrp) : null);
    const isMrpAiEstimated = !parsePrice(product?.product_price || product?.MRP || product?.mrp || product?.price) && !!aiData?.mrp && aiData?.mrpSource === 'ai_estimated';
    const isMrpScrapedLive = !parsePrice(product?.product_price || product?.MRP || product?.mrp || product?.price) && !!aiData?.mrp && (aiData?.mrpSource || '').includes('scraped_live');
    // Also use scrapedMrp field from DB if available
    const dbScrapedMrp = parsePrice(product?.scrapedMrp);
    const effectiveMrp = parsePrice(product?.product_price || product?.MRP || product?.mrp || product?.price)
        || dbScrapedMrp
        || (aiData?.mrp ? parseFloat(aiData.mrp) : null);
    const showScrapedBadge = (isMrpScrapedLive || !!dbScrapedMrp) && !parsePrice(product?.product_price || product?.MRP || product?.mrp || product?.price);
    const showAiBadge = isMrpAiEstimated && !showScrapedBadge;
    const companyName = formatCompanyName(
        decodeHtml(getProductField('product_manufactured', 'Company', 'company', 'brand') || aiData?.company || '')
    );
    const pPack = getProductField('Pack', 'pack', 'packSize') || (aiData?.pack || '');
    const pUnit = getProductField('Unit', 'unit', 'measurementUnit') || (aiData?.unit || '');
    const unitInfo = formatPackingInfo(pPack, pUnit, getProductField('sub_category', 'Category', 'category'), productName);
    
    const rawDescription = getProductField('medicine_desc', 'description', 'Description') || (aiData?.description ? aiData.description : null);
    const isDescriptionAi = !getProductField('medicine_desc', 'description', 'Description') && !!aiData?.description;
    const rawIndications = getProductField('sub_category', 'Category', 'category', 'indications') || aiData?.category;
    const rawInteractions = getProductField('drug_interactions', 'drugInteraction', 'drugInteractions', 'DrugInteraction');
    const saltComposition = getProductField('saltComposition', 'salt_composition', 'composition') || aiData?.saltComposition;

    // Rich Medical Schema Fields
    const introduction = getProductField('introduction');
    const primaryUse = getProductField('primaryUse');
    const benefits = getProductField('benefits');
    const sideEffects = getProductField('side_effects', 'sideEffects', 'SideEffects', 'sideEffect', 'SideEffect');
    const howToUse = getProductField('howToUse');
    const howItWorks = getProductField('howItWorks');
    const alcoholInteraction = getProductField('alcoholInteraction');
    const pregnancyInteraction = getProductField('pregnancyInteraction');
    const lactationInteraction = getProductField('lactationInteraction');
    const drivingInteraction = getProductField('drivingInteraction');
    const kidneyInteraction = getProductField('kidneyInteraction');
    const liverInteraction = getProductField('liverInteraction');

    // Resolve image: DB image → AI image → placeholder
    const dbImg = getCleanImageUrl(product, productName);
    const aiImg = aiData?.imageUrl || null;
    const img = (dbImg && !dbImg.includes('placehold.co')) ? dbImg : (aiImg || dbImg);
    const imageUrls = product?.imageUrls?.length > 0 ? product.imageUrls : (aiData?.imageUrls?.length > 0 ? aiData.imageUrls : [img]);

    useEffect(() => {
        // Reset failed state when image changes
        setImageFailed(false);
        setDisplayImg(imageUrls[0] || img);
    }, [img, product, imageUrls]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-sky-600"></div>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
                <p className="text-gray-700 text-lg">{error || "Product could not be loaded."}</p>
            </div>
        );
    }

    let formattedInteractions = null;
    if (rawInteractions) {
        let parsed = rawInteractions;
        
        // If it's a JSON string, try to parse it
        if (typeof rawInteractions === 'string' && rawInteractions.trim().startsWith('{')) {
            try {
                parsed = JSON.parse(rawInteractions);
            } catch (e) {
                // Keep as string if parsing fails
            }
        }

        // If parsed is an object, format it
        if (typeof parsed === 'object' && parsed !== null) {
            if (parsed.drug && Array.isArray(parsed.drug)) {
                if (parsed.drug.length > 0) {
                    const list = parsed.drug.map((d, i) => {
                        const brand = parsed.brand && parsed.brand[i] ? ` (${String(parsed.brand[i]).trim()})` : '';
                        const effect = parsed.effect && parsed.effect[i] ? ` - ${parsed.effect[i]}` : '';
                        return `${d}${brand}${effect}`;
                    });
                    formattedInteractions = list.join(', ');
                } else {
                    formattedInteractions = null; // Don't stringify empty arrays
                }
            } else {
                // If it's an object but doesn't have the expected array structure, stringify it to avoid React crashes
                if (Object.keys(parsed).length === 0) {
                    formattedInteractions = null;
                } else {
                    formattedInteractions = JSON.stringify(parsed);
                }
            }
        } else {
            // It's just a regular string
            formattedInteractions = parsed;
        }
    }

    let descriptionText = rawDescription || 'Not available';
    let extractedIndications = '';

    if (rawDescription) {
        // Split by period followed by a space or end of string to handle sentences
        const sentences = rawDescription.split(/\.\s+|\.$/);
        const descSentences = [];
        const indSentences = [];
        
        sentences.forEach(sentence => {
            if (!sentence.trim()) return;
            const lower = sentence.toLowerCase();
            if (lower.includes('used to treat') || lower.includes('used in the treatment') || lower.includes('indicated for') || lower.includes('used for')) {
                indSentences.push(sentence.trim() + '.');
            } else {
                descSentences.push(sentence.trim());
            }
        });
        
        if (indSentences.length > 0) {
            extractedIndications = indSentences.join(' ');
            descriptionText = descSentences.join('. ') + (descSentences.length > 0 ? '.' : '');
        }
    }

    const finalImg = displayImg || imageUrls[0] || img;
    const shouldShowImage = true;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-12 border border-gray-100">
                <div className="flex flex-col md:flex-row">
                    {/* Left: Product Image */}
                    {shouldShowImage && (
                        <div className="w-full md:w-5/12 bg-white flex flex-col p-4 sm:p-8 lg:p-12 relative border-b md:border-b-0 md:border-r border-gray-100">
                            <div className="flex-1 flex items-center justify-center mb-6 min-h-[260px] bg-white rounded-2xl p-4">
                                <img 
                                    src={finalImg} 
                                    alt={productName} 
                                    className="max-h-80 max-w-full object-contain transform hover:scale-105 transition-transform duration-500 drop-shadow-sm" 
                                    style={{
                                        imageRendering: '-webkit-optimize-contrast',
                                        transform: 'translateZ(0)'
                                    }}
                                    onError={(e) => {
                                        if (!imageFailed) {
                                            setImageFailed(true);
                                            const fallback = getPlaceholderImage(productName);
                                            setDisplayImg(fallback);
                                            e.target.src = fallback;
                                        }
                                    }}
                                />
                            </div>
                            
                            {/* Image Thumbnails Gallery */}
                            {imageUrls.length > 1 && (
                                <div className="flex justify-center gap-3 mt-auto overflow-x-auto pb-2">
                                    {imageUrls.map((url, idx) => (
                                        <button 
                                            key={idx}
                                            onClick={() => setDisplayImg(url)}
                                            className={`flex-shrink-0 w-16 h-16 rounded-xl border-2 overflow-hidden transition-all ${
                                                finalImg === url ? 'border-sky-500 shadow-md ring-2 ring-sky-200 ring-offset-1' : 'border-gray-200 hover:border-sky-300 opacity-70 hover:opacity-100'
                                            }`}
                                        >
                                            <img src={url} alt={`${productName} view ${idx + 1}`} className="w-full h-full object-contain bg-white" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Right: Product Info */}
                    <div className={`w-full ${shouldShowImage ? 'md:w-7/12' : ''} p-8 lg:p-12 flex flex-col h-full`}>
                        {companyName && (
                            <p className="text-sm md:text-base text-sky-600 font-bold tracking-wide mb-3">
                                {companyName}
                            </p>
                        )}
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
                            {productName}
                        </h1>
                        {unitInfo && <p className="text-lg text-gray-500 mb-8 font-medium">{unitInfo}</p>}
                        


                        <div className="space-y-6 mb-10">
                            {/* Composition */}
                            {saltComposition && (
                                <div>
                                    <span className="text-gray-500 font-semibold block mb-1 uppercase text-xs tracking-wider">Composition</span>
                                    <span className="text-gray-800 text-base leading-relaxed">{saltComposition}</span>
                                </div>
                            )}
                        </div>
                        
                        <div className="mt-auto flex flex-col sm:flex-row items-start sm:items-center justify-between border-t border-gray-100 pt-8">
                            <div className="mb-4 sm:mb-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm text-gray-400 font-medium">MRP Price</span>
                                    {showScrapedBadge && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-4H7l5-8v4h4l-5 8z"/></svg>
                                            Live Price
                                        </span>
                                    )}
                                    {showAiBadge && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-600 text-[10px] font-bold">
                                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                                            AI Estimated
                                        </span>
                                    )}
                                </div>
                                <div className="text-4xl lg:text-5xl font-bold text-gray-900">
                                    {effectiveMrp ? `₹${effectiveMrp.toFixed(2)}` : 'N/A'}
                                </div>
                                <p className="text-xs text-gray-500 mt-2 italic max-w-[200px]">* Price may vary. Actual price will be reflected in the billed amount.</p>
                            </div>
                            <button 
                                onClick={() => addToCart({ ...product, imageUrl: finalImg })} 
                                className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-3.5 px-8 rounded-2xl shadow-lg shadow-sky-600/30 transition-all flex items-center justify-center gap-3 w-full sm:w-auto hover:-translate-y-0.5 active:translate-y-0 text-lg"
                                disabled={!mrpValue}
                            >
                                Add to Cart
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Rich Medical Data Section */}
            <RichMedicalData 
                introduction={introduction}
                primaryUse={primaryUse}
                benefits={benefits}
                sideEffects={sideEffects}
                howToUse={howToUse}
                howItWorks={howItWorks}
                alcoholInteraction={alcoholInteraction}
                pregnancyInteraction={pregnancyInteraction}
                lactationInteraction={lactationInteraction}
                drivingInteraction={drivingInteraction}
                kidneyInteraction={kidneyInteraction}
                liverInteraction={liverInteraction}
                fallbackDescription={descriptionText}
                fallbackIndications={extractedIndications}
            />

            {/* Substitutes Section */}
            <div className="mt-16">
                <h3 className="text-2xl lg:text-3xl font-extrabold text-gray-900 mb-8 flex items-center gap-3">
                    <span className="bg-sky-100 text-sky-600 p-2 rounded-xl">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                    </span>
                    Generic Substitutes
                </h3>

                {/* AI Substitutes loading (Silenced to avoid disturbing user) */}
                {aiSubsLoading && null}

                {/* DB Substitutes */}
                {!loading && substitutes.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {substitutes.map(substitute => (
                            <ProductCard
                                key={substitute._id}
                                product={substitute}
                                addToCart={addToCart}
                            />
                        ))}
                    </div>
                )}

                {/* AI-Suggested Generic Substitutes */}
                {!loading && substitutes.length === 0 && aiSubstitutes.length > 0 && (
                    <div>
                        <div className="flex items-center gap-2 mb-6">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-100 text-violet-600 text-xs font-bold">
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm0 18a8 8 0 110-16 8 8 0 010 16zm-1-5h2v2h-2zm0-8h2v6h-2z"/></svg>
                                AI Suggested Generics
                            </span>
                            <span className="text-sm text-gray-400">Same composition, potentially lower price</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {aiSubstitutes.map((sub, idx) => (
                                <div key={idx} className="bg-white border border-violet-100 rounded-2xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex-1">
                                            <p className="text-[10px] text-violet-500 font-bold uppercase tracking-wider mb-1">{sub.company}</p>
                                            <h4 className="font-bold text-slate-800 text-sm leading-tight">{sub.name}</h4>
                                        </div>
                                        {sub.savingsPercent > 0 && (
                                            <span className="ml-2 shrink-0 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                                Save {sub.savingsPercent}%
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400 mb-3 line-clamp-2">{sub.saltComposition}</p>
                                    <div className="flex items-center justify-between border-t border-slate-50 pt-3">
                                        <div>
                                            <span className="text-[9px] text-slate-400 uppercase tracking-wider">Est. MRP</span>
                                            <p className="font-extrabold text-slate-900 text-lg">₹{Number(sub.mrp).toFixed(2)}</p>
                                        </div>
                                        <span className="text-[10px] text-violet-400 font-semibold bg-violet-50 px-2 py-1 rounded-lg">{sub.category}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Nothing at all */}
                {!loading && !aiSubsLoading && substitutes.length === 0 && aiSubstitutes.length === 0 && (
                    <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200 border-dashed">
                        <p className="text-gray-500 text-lg">No generic substitutes found for this product.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductDetailsView;
